import { useMemo } from 'react';
import type { SWRConfiguration } from 'swr';
import { useCore } from '$api-clients/hooks';

/**
 * Fetches and normalizes interaction data using the appropriate
 * type-specific endpoint when available:
 *
 * - `/listings/{listingId}` for listings
 * - `/offers/{offerId}` for offers
 * - `/referrals/{referralId}` for referrals
 * - `/client-groups/{clientGroupId}` for client groups
 * - `/interactions/{interactionId}` for all other types
 *
 * `data` is enriched with `status`, `representation`, `isArchived`,
 * `isIntermediary`, `hasUnrepresentedClient`, `acceptedOfferId`,
 * `associatedListingId`, `clientGroupId`, `onMarketDate`, `listingEndDate`,
 * and `showOnInsideListings` so consumers don't need `'field' in interaction`
 * guards.
 */
export function useInteraction(
  interactionId: string | undefined,
  interactionType: string | undefined,
  skip = false,
  swrConfig?: SWRConfiguration,
) {
  const shouldSkip = !interactionId || !interactionType || skip;

  const isListing = interactionType === 'listing';
  const isOffer = interactionType === 'offer';
  const isReferral = interactionType === 'referral';
  const isClientGroup = interactionType === 'clientGroup';
  const isTyped = isListing || isOffer || isReferral || isClientGroup;

  const { data: listingData, ...listingRest } = useCore(
    '/listings/{listingId}',
    !shouldSkip && isListing
      ? { params: { path: { listingId: interactionId } } }
      : null,
    swrConfig,
  );

  const { data: offerData, ...offerRest } = useCore(
    '/offers/{offerId}',
    !shouldSkip && isOffer
      ? { params: { path: { offerId: interactionId } } }
      : null,
    swrConfig,
  );

  const { data: referralData, ...referralRest } = useCore(
    '/referrals/{referralId}',
    !shouldSkip && isReferral
      ? { params: { path: { referralId: interactionId } } }
      : null,
    swrConfig,
  );

  const { data: clientGroupData, ...clientGroupRest } = useCore(
    '/client-groups/{clientGroupId}',
    !shouldSkip && isClientGroup
      ? { params: { path: { clientGroupId: interactionId } } }
      : null,
    swrConfig,
  );

  const { data: genericData, ...genericRest } = useCore(
    '/interactions/{interactionId}',
    !shouldSkip && !isTyped ? { params: { path: { interactionId } } } : null,
    swrConfig,
  );

  function getActiveResult() {
    if (isListing) return { interaction: listingData, rest: listingRest };
    if (isOffer) return { interaction: offerData, rest: offerRest };
    if (isReferral) return { interaction: referralData, rest: referralRest };
    if (isClientGroup)
      return { interaction: clientGroupData, rest: clientGroupRest };
    return { interaction: genericData?.interaction, rest: genericRest };
  }

  const { interaction, rest } = getActiveResult();

  const status =
    interaction && 'status' in interaction ? interaction.status : undefined;
  const representation =
    interaction && 'representation' in interaction
      ? interaction.representation
      : null;
  const isArchived =
    interaction && 'isArchived' in interaction ? interaction.isArchived : false;
  const isIntermediary =
    interaction && 'isIntermediary' in interaction
      ? Boolean(interaction.isIntermediary)
      : false;
  const showOnInsideListings =
    interaction && 'showOnInsideListings' in interaction
      ? Boolean(interaction.showOnInsideListings)
      : false;
  const hasUnrepresentedClient =
    interaction && 'hasUnrepresentedClient' in interaction
      ? Boolean(interaction.hasUnrepresentedClient)
      : false;
  const acceptedOfferId =
    interaction && 'acceptedOfferId' in interaction
      ? interaction.acceptedOfferId
      : null;
  const associatedListingId =
    interaction && 'associatedListingId' in interaction
      ? interaction.associatedListingId
      : null;
  const clientGroupId =
    interaction && 'clientGroupId' in interaction
      ? interaction.clientGroupId
      : null;

  const listing =
    interaction && 'listing' in interaction ? interaction.listing : null;

  let property = null;
  if (interaction && 'property' in interaction) {
    property = interaction.property;
  } else if (listing?.property) {
    property = listing.property;
  }

  const onMarketDate =
    (interaction && 'onMarketDate' in interaction
      ? interaction.onMarketDate
      : listing?.onMarketDate) ?? null;
  const listingEndDate =
    (interaction && 'listingEndDate' in interaction
      ? interaction.listingEndDate
      : listing?.listingEndDate) ?? null;

  // Memoized so consumers can put `data` in dependency arrays: the SWR payload
  // is stable between revalidations, but this wrapper object would not be.
  const data = useMemo(
    () =>
      interaction
        ? {
            ...interaction,
            status,
            representation,
            isArchived,
            isIntermediary,
            property,
            onMarketDate,
            listingEndDate,
            showOnInsideListings,
            hasUnrepresentedClient,
            acceptedOfferId,
            associatedListingId,
            clientGroupId,
          }
        : undefined,
    [
      interaction,
      status,
      representation,
      isArchived,
      isIntermediary,
      property,
      onMarketDate,
      listingEndDate,
      showOnInsideListings,
      hasUnrepresentedClient,
      acceptedOfferId,
      associatedListingId,
      clientGroupId,
    ],
  );

  return { ...rest, data };
}
